import ErrorBoundary from "@/components/common/ErrorBoundary";
import PageHeader from "@/components/common/PageHeader";
import DataTable from "@/components/datatable/components/DataTable";
import { safeDecompress, tableSearchSchema } from "@/components/datatable/hooks/useTableUrlState";
import { mapTableStateToQueryParams } from "@/components/datatable/utils";
import useConfirm from "@/hooks/useConfirm";
import useICashTransactionsQuery from "@/hooks/useICashTransactionsQuery";
import { icashTransactionsKeys } from "@/query-keys";
import type { ICashTransactionListItem } from "@/schemas/icashTransactionSchema";
import { services } from "@/services";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import NewICashTransaction from "./_components/NewICashTransaction";
import useColumns from "./_hooks/useColumns";
import useICashTransactionMutation from "./_hooks/useICashTransactionMutation";

export const Route = createFileRoute("/_private/icash-transactions/")({
  validateSearch: tableSearchSchema,
  loaderDeps: ({ search }) => ({
    q: search.q,
  }),
  loader: async ({ deps, context: { queryClient } }) => {
    const tableState = safeDecompress(deps.q);
    const requestParams = mapTableStateToQueryParams(tableState);
    return queryClient.ensureQueryData({
      queryKey: icashTransactionsKeys.lists(deps.q),
      queryFn: () => services.getICashTransactions(requestParams),
    });
  },
  component: RouteComponent,
  errorComponent: ({ error }) => (
    <ErrorBoundary error={error} reset={() => window.location.reload()} />
  ),
});

function RouteComponent() {
  const search = Route.useSearch();
  const loaderData = Route.useLoaderData();

  const columns = useColumns();
  const { t } = useTranslation();

  const { data: paginatedTransactions } = useICashTransactionsQuery({
    q: search.q!,
    initialData: loaderData,
  });
  const { deleteTransaction, isDeleting } = useICashTransactionMutation();

  const deleteConfirm = useConfirm({
    title: t("icashTransactions.confirm.delete.title"),
    description: t("icashTransactions.confirm.delete.description"),
    isPending: isDeleting,
  });

  const handleDelete = useCallback(
    (icashTransaction: ICashTransactionListItem) =>
      deleteConfirm.confirm(() => void deleteTransaction(icashTransaction.id)),
    [deleteConfirm, deleteTransaction],
  );

  return (
    <>
      <PageHeader
        title={t("icashTransactions.list.title")}
        subtitle={t("icashTransactions.list.subtitle")}
        action={<NewICashTransaction />}
      />
      <DataTable<ICashTransactionListItem>
        searchPlaceholder={t("icashTransactions.list.searchPlaceholder")}
        columns={columns}
        data={paginatedTransactions?.items}
        nextCursor={paginatedTransactions?.nextCursor}
        hasMore={paginatedTransactions?.hasMore}
        queryKey={icashTransactionsKeys.lists(search.q)}
        navigateFrom="/icash-transactions"
        searchFrom="/_private/icash-transactions/"
        onDelete={handleDelete}
        searchable={true}
      />
      {deleteConfirm.confirmDialog}
    </>
  );
}
